<template>
  <el-dialog
    title="地图选点"
    :visible.sync="innerVisible"
    width="860px"
    append-to-body
    @opened="handleOpened"
    @closed="handleClosed"
  >
    <div v-loading="mapLoading" class="coord-picker">
      <div class="picker-search">
        <el-input
          v-model="searchText"
          clearable
          placeholder="搜索地址、小区、道路、POI"
          prefix-icon="el-icon-search"
          @keyup.enter.native="searchPlace"
        >
          <el-button slot="append" @click="searchPlace">搜索</el-button>
        </el-input>
      </div>
      <div ref="mapEl" class="picker-map"></div>
      <div class="picker-foot">
        <span>经度：<strong>{{ displayLng }}</strong></span>
        <span>纬度：<strong>{{ displayLat }}</strong></span>
        <span class="picker-tip">点击地图选点，或拖动标记调整位置</span>
      </div>
    </div>
    <span slot="footer">
      <el-button @click="innerVisible = false">取消</el-button>
      <el-button type="primary" :disabled="!hasCoord" @click="confirmPick">确定</el-button>
    </span>
  </el-dialog>
</template>

<script>
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { formatCoord, geocodeKeyword, getAmapKey, preloadAmapSearch } from '@/utils/amapLoader'
import { createCoordPickerMap, buildPickerMarkerIcon, SHANGHAI_MAP_CENTER, SHANGHAI_MAP_ZOOM } from '@/utils/leafletMap'

export default {
  name: 'DeviceCoordPicker',
  props: {
    visible: { type: Boolean, default: false },
    longitude: { type: [String, Number], default: '' },
    latitude: { type: [String, Number], default: '' },
    addressHint: { type: String, default: '' }
  },
  data() {
    return {
      innerVisible: false,
      mapLoading: false,
      searchText: '',
      lng: '',
      lat: '',
      map: null,
      marker: null
    }
  },
  computed: {
    displayLng() {
      return this.lng !== '' ? this.lng : '—'
    },
    displayLat() {
      return this.lat !== '' ? this.lat : '—'
    },
    hasCoord() {
      return this.lng !== '' && this.lat !== ''
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        this.innerVisible = val
      }
    },
    innerVisible(val) {
      this.$emit('update:visible', val)
    }
  },
  methods: {
    async handleOpened() {
      this.lng = formatCoord(this.longitude)
      this.lat = formatCoord(this.latitude)
      this.searchText = this.addressHint || ''
      this.mapLoading = true
      try {
        await this.$nextTick()
        await this.waitForDialogLayout()
        this.initMap()
        this.refreshMapSize()
        if (!this.hasCoord) {
          this.map.setView(SHANGHAI_MAP_CENTER, SHANGHAI_MAP_ZOOM)
        }
        if (getAmapKey()) {
          preloadAmapSearch().catch(() => {})
        }
      } catch (e) {
        this.$message.error(e.message || '地图加载失败')
      } finally {
        this.mapLoading = false
      }
    },
    handleClosed() {
      this.destroyMap()
    },
    waitForDialogLayout() {
      return new Promise(resolve => setTimeout(resolve, 120))
    },
    refreshMapSize() {
      if (!this.map) return
      this.map.invalidateSize(true)
      setTimeout(() => this.map && this.map.invalidateSize(true), 280)
    },
    parseCenter() {
      const lng = Number(this.longitude)
      const lat = Number(this.latitude)
      if (!Number.isNaN(lng) && !Number.isNaN(lat)) return [lat, lng]
      return SHANGHAI_MAP_CENTER
    },
    initMap() {
      this.destroyMap()
      const center = this.parseCenter()
      const zoom = this.hasCoord ? 16 : SHANGHAI_MAP_ZOOM
      this.map = createCoordPickerMap(this.$refs.mapEl, { center, zoom })
      this.marker = L.marker(center, {
        icon: buildPickerMarkerIcon(),
        draggable: true
      }).addTo(this.map)

      if (this.hasCoord) {
        this.setCoord(center[1], center[0], false)
      } else {
        this.lng = ''
        this.lat = ''
      }

      this.marker.on('dragend', () => {
        const { lat, lng } = this.marker.getLatLng()
        this.setCoord(lng, lat, false)
      })
      this.map.on('click', e => {
        const { lat, lng } = e.latlng
        this.marker.setLatLng([lat, lng])
        this.setCoord(lng, lat, false)
      })
    },
    setCoord(lng, lat, moveMap = true) {
      this.lng = formatCoord(lng)
      this.lat = formatCoord(lat)
      if (moveMap && this.map && this.marker) {
        const latLng = [Number(this.lat), Number(this.lng)]
        this.marker.setLatLng(latLng)
        this.map.setView(latLng, Math.max(this.map.getZoom(), 16))
      }
    },
    async searchPlace(showEmptyMsg = true) {
      const keyword = (this.searchText || '').trim()
      if (!keyword) {
        if (showEmptyMsg) this.$message.warning('请输入搜索关键词')
        return
      }
      this.mapLoading = true
      try {
        const loc = await geocodeKeyword(keyword)
        if (loc) {
          if (this.marker) this.marker.setLatLng([loc.lat, loc.lng])
          this.setCoord(loc.lng, loc.lat)
          if (this.map) this.map.setView([loc.lat, loc.lng], 16)
          return
        }
        if (showEmptyMsg) {
          this.$message.warning('未找到相关地址，请换关键词或在地图上点击选点')
        }
      } catch (e) {
        if (showEmptyMsg) {
          const msg = (e && e.message) || '地址搜索失败'
          if (msg.includes('Key') || msg.includes('配置')) {
            this.$message.error('地址搜索暂不可用，请配置高德地图 Key，或在地图上点击选点')
          } else if (msg.includes('超时')) {
            this.$message.error('地址搜索超时，请稍后重试或在地图上点击选点')
          } else if (msg.includes('服务调用失败') || msg.includes('Network') || msg.includes('I/O') || msg.includes('socket')) {
            this.$message.error('地址搜索服务连接失败，请检查服务器网络或地图 Key，或在地图上点击选点')
          } else {
            this.$message.error(msg)
          }
        }
      } finally {
        this.mapLoading = false
      }
    },
    confirmPick() {
      if (!this.hasCoord) return
      this.$emit('confirm', {
        longitude: this.lng,
        latitude: this.lat
      })
      this.innerVisible = false
    },
    destroyMap() {
      if (this.map) {
        this.map.remove()
        this.map = null
      }
      this.marker = null
    }
  }
}
</script>

<style scoped lang="scss">
.coord-picker {
  min-height: 420px;
}
.picker-search {
  margin-bottom: 10px;
}
.picker-map {
  width: 100%;
  height: 420px;
  border: 1px solid #e5e6eb;
  border-radius: 4px;
  background: #f2f3f5;
  z-index: 0;
}
.picker-foot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  margin-top: 10px;
  font-size: 13px;
  color: #4e5969;
}
.picker-foot strong {
  color: #1d2129;
  font-weight: 600;
}
.picker-tip {
  color: #86909c;
  font-size: 12px;
}
</style>

<style lang="scss">
.coord-picker-marker-wrap {
  background: transparent;
  border: none;
}
.coord-picker-marker {
  display: block;
  width: 18px;
  height: 18px;
  margin: 2px;
  background: #1677ff;
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
}
.picker-map .leaflet-control-zoom {
  border: none;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
}
.coord-picker .el-loading-fade-leave,
.coord-picker .el-loading-fade-leave-active {
  pointer-events: none;
}
</style>
